/**
 * The assistant's state, and the one rule that shapes all of it.
 *
 * She may prepare and she may explain. She may not commit. Every path in this
 * file honours that structurally rather than by convention: `draft()` stores a
 * `commit` closure and never invokes it — only the button in the panel does,
 * under the operator's finger — and suggestions carry no callable at all.
 *
 * The order she looks things up in is deliberate. Platform records first,
 * always: an operator asking about A2 is asking about this airfield, and the
 * answer is in the work orders, not on the web. The outside is consulted only
 * when it is configured and the question plausibly reaches past the fence — a
 * regulation's wording, a limit, a standard. When there is no model connected
 * she says exactly that, because "nothing is connected" and "I looked and found
 * nothing" are opposite claims and only one of them is true.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyFigures } from "../domain/decision";
import { atLeast, canSee, screenLevel, refusalFor, routeForTopic, topicLevel, useAccessLevel, type AccessLevel } from "./access";
import * as learning from "./learning";
import { arrivalLine, describeRoute, getScreen, isAboutScreen, subscribeScreen, type ScreenContext } from "./screen";
import { useSession } from "../app/SessionContext";
import { consequenceOf, type ConsequenceContext } from "./consequences";
import { firstName } from "./greeting";
import { briefing, isStatusQuestion, isTriageQuestion, searchLocal, workOrderRef } from "./local";
import { statusOf, urgent } from "./digest";
import { describeWorkOrder, isPlanRequest, isScheduleRequest, parsePlan, parseWorkOrder } from "./tasks";
import { findModule, findTerm, isExplainRequest, isTourRequest, tour } from "./guide";
import { answerForTopic, isAboutAStandard, pageTopic } from "./pages";
import { analyseSection, sectionFor } from "./sections";
import { CURRENCY, isStandardsQuestion, standardsAnswer } from "./standards";
import { briefingFor, departmentIn, isBriefingRequest } from "./briefings";
import { acknowledgement, interruptionWords, isGreeting, isInterruption, isSignOff, whatWouldYouLike } from "./chat";
import { loadPreferences, principalKey, savePreferences, type Preferences } from "./preferences";
import { loadReminders, newReminder, ordered, saveReminders, type Reminder } from "./reminders";
import * as voice from "./voice";
import * as ears from "./listen";
import { loadDueState, saveDueState, scanDue, unseen, type DueItem, type DueState } from "./due";
import {
  ExternalUnavailable,
  askModel,
  hasModel,
  hasSearch,
  platformModel,
  loadConfig,
  saveConfig,
  searchWeb,
  type ExternalConfig,
  type HistoryTurn,
} from "./external";
import { looksLikeFollowUp, refusal, render as renderReply } from "./platform";
import type { ActionEvent, Draft, MarkState, Source, Suggestion, Turn } from "./types";
import { useNavLayout } from "../layout/NavLayoutContext";
import { labelOf } from "../layout/navigation";
import * as stage from "./stage/director";
import { findByText, findWords, nameOf } from "./stage/anchors";
import { showIntent, type ShowIntent } from "./stage/intents";
import { planWithModel } from "./stage/planner";
import { findScreen, findSuite, screenList, screenTour, screenVisit, siteTour, suiteTour, whereIsTarget } from "./stage/scripts";
import { DataSourceError, type SkyConverseReply } from "../api/dataSource";
import type { WorkOrderKind, WorkOrderPriority } from "../domain/workOrders";
import { orderWithModel, review as reviewSystem, reviewLines, type Finding, type Review } from "./review";

let seq = 0;
const nextId = () => `t${++seq}`;

/**
 * What she is told about herself. Kept here, in the open, because a system
 * prompt that decides how a safety tool speaks is not an implementation detail.
 */
export const SYSTEM_PROMPT = [
  "Your name is Sky. You assist an airfield operations team at an airport, inside their maintenance platform.",
  "You never make or announce a decision. You lay out options and their trade-offs and stop.",
  "You never congratulate the operator on a decision; their outcomes are not known when they act.",
  "If the platform's own records answer the question, use them and say so.",
  "If you do not know, say you do not know. Do not fill a gap with a plausible number.",
  "Be brief. An operator is reading this mid-task.",
  "When asked how something works, explain the mechanism, and say plainly when you do not know rather than guessing at a field's meaning.",
].join(" ");

/**
 * How she speaks when a model words her answers. The facts come from the
 * platform; this decides only the voice, and the figure check decides whether
 * the voice is used at all.
 */
export const CONVERSE_PROMPT = [
  SYSTEM_PROMPT,
  "You are in a conversation with the operator. The platform has already looked the answer up and hands it to you as material.",
  "Say it in your own words, as a knowledgeable colleague would: two to four short sentences that read well aloud. No headings, no bullet lists.",
  "Use only the facts in the material. Do not add any number, name, date or record that is not there. Keep record references exactly as written.",
  "If the question refers to something said earlier in the conversation, follow it. If the material does not answer what was asked, say so plainly and say what it does show.",
  "Reply in the language the operator wrote in.",
].join(" ");

/** What a conversation reply stood on: each lookup the platform ran, then the model. */
function agentSources(reply: SkyConverseReply): Source[] {
  return [
    ...reply.steps.map((s) => ({
      kind: "local" as const,
      label: s.intent.replace(/^query_/, "").replace(/_/g, " "),
      detail: `${s.rowCount} row${s.rowCount === 1 ? "" : "s"}, run as you`,
    })),
    {
      kind: "external" as const,
      label: reply.model,
      detail: reply.withheld
        ? "its wording named a figure no lookup returned, so the platform's stands"
        : "chose the lookups; every figure checked against them",
    },
    ...(reply.general
      ? [
          {
            kind: "external" as const,
            label: "General knowledge",
            detail: "the paragraph marked so is the model's own knowledge, not your records",
          },
        ]
      : []),
  ];
}

/** What was said after her name: "hey Sky, what is open" → "what is open". */
export function afterWakeWord(heard: string): string {
  const m = heard.match(/\b(?:hey\s+)?(?:sky|skye|skie|assistant)\b[\s,.!?:;-]*(.*)$/i);
  return (m ? m[1] : heard).trim();
}

/** "Hey Sky, what is open" → "what is open"; a question without her name is left alone. */
export function withoutHerName(heard: string): string {
  return /^\s*(?:hey\s+|hi\s+|ok(?:ay)?\s+)?(?:sky|skye|skie)\b/i.test(heard) ? afterWakeWord(heard) : heard.trim();
}

/** Long enough for a model to answer; short enough that a spoken question is not left hanging. */
const COMPOSE_TIMEOUT_MS = 15_000;

/** Every figure in these texts, in the forms `verifyFigures` compares. */
export function figuresIn(...texts: string[]): Set<string> {
  const out = new Set<string>();
  for (const t of texts)
    for (const f of t.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
      out.add(f);
      out.add(f.replace(/,/g, ""));
      out.add(String(Number(f.replace(/,/g, ""))));
    }
  return out;
}

export interface AssistantApi {
  open: boolean;
  turns: readonly Turn[];
  mark: MarkState;
  unread: number;
  config: ExternalConfig;
  setConfig: (c: ExternalConfig) => void;
  /** Open the dock, optionally with a question already asked. */
  summon: (question?: string) => void;
  dismiss: () => void;
  ask: (question: string) => Promise<void>;
  /** Tell her the operator did something, so she can say what follows. */
  noteAction: (event: ActionEvent, context?: Partial<ConsequenceContext>) => void;
  /** Put prepared work in front of the operator. Never commits it. */
  propose: (text: string, draft: Draft, suggestions?: Suggestion[]) => void;
  /** The rest of an answer, when the operator asks for it. */
  expand: (lines: readonly string[]) => void;
  clear: () => void;

  /** The key this operator's preferences and reminders are stored under. */
  who: string;
  prefs: Preferences;
  setPrefs: (p: Preferences) => void;
  /** the access level in force: what she may read, say and open */
  level: AccessLevel;
  /** what the person is looking at, when the screen has said */
  screen: ScreenContext | null;

  /** Reading aloud. Never happens on its own; every call is a press or a setting. */
  speaking: boolean;
  readAloud: (text: string) => void;
  stopSpeaking: () => void;

  /**
   * Whether she is on screen at all.
   *
   * Session state, not a preference: she withdraws a few seconds after the
   * greeting and comes back when she is clicked or spoken to. `prefs.hidden` is
   * the different, deliberate act of putting her away for good.
   */
  present: boolean;
  show: () => void;
  withdraw: () => void;
  /** Listening for her name in the background, so she can be spoken to. */
  waking: boolean;
  /**
   * A conversation is running: she re-opens the microphone after each answer
   * until the operator signs off. Started by her name or by the microphone,
   * ended by "thanks", by Stop, or by closing her.
   */
  conversing: boolean;
  endConversation: () => void;

  /** Taking a spoken instruction. Stops on its own once a sentence lands. */
  listening: boolean;
  heard: string;
  startListening: () => void;
  /**
   * She was called by name and is on a call: the arrival look, full size,
   * talking and listening until the person ends it. Typing is offered, not
   * assumed.
   */
  onCall: boolean;
  /** Hang up: she stops listening, stays quiet, and goes back to her corner. */
  endCall: () => void;
  stopListening: () => void;
  /**
   * The microphone could not be opened in this session — refused by the
   * browser or the host, or absent. Said once; after that she does not try
   * again by herself, and the typing box is the way in. `startListening`
   * is the retry.
   */
  micBlocked: string | null;
  /** Signed off: she is fading out on her way to the corner. */
  leaving: boolean;

  /** Certificates, reviews and services coming due that she has not yet raised. */
  due: readonly DueItem[];
  /** Heard, but warn me again at the next band. */
  ackDue: (id: string) => void;
  /** Cancelled: never warn about this again. */
  stopDue: (id: string) => void;

  reminders: readonly Reminder[];
  addReminder: (text: string, dueAt: number, about?: Reminder["about"]) => void;
  completeReminder: (id: string) => void;
  removeReminder: (id: string) => void;
}

const Ctx = createContext<AssistantApi | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  /* Which screen the operator is on. Part of the question, not decoration:
     "what is the condition" means the survey on the friction page and something
     else entirely on the dashboard. */
  const { pathname } = useLocation();
  const routeRef = useRef(pathname);
  useEffect(() => {
    routeRef.current = pathname;
  }, [pathname]);
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  /* What has been said, for a model that should follow the conversation. */
  const turnsRef = useRef(turns);
  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);
  const [mark, setMark] = useState<MarkState>("idle");
  const [unread, setUnread] = useState(0);
  const [config, setConfigState] = useState<ExternalConfig>(() => loadConfig());
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  const abort = useRef<AbortController | null>(null);
  /* The platform-side conversation, so a follow-up has something to follow.
     Survives the dock being closed; the platform expires it on its own clock. */
  const sessionRef = useRef<string | undefined>(undefined);

  /*
   * An answer to a spoken question is spoken.
   *
   * `voice.enabled` — "read every answer aloud" — stays off by default, and
   * should: an operator mid-task does not want a paragraph narrated at them.
   * But it was also the only thing that made her audible, so hands-free meant
   * the operator talking and her replying in writing, which is half a
   * conversation and the wrong half.
   *
   * These two say "this question arrived by microphone", and only that. She
   * answers aloud when spoken to and stays quiet when typed at, which is the
   * distinction the setting was reaching for. `heardIt` is armed by the
   * recogniser and consumed by the next `ask`; `answerAloud` then holds for
   * that whole answer, so a lookup that speaks an acknowledgement first does
   * not swallow the turn that matters.
   */
  const heardIt = useRef(false);
  const answerAloud = useRef(false);
  /* `ask` builds drafts whose commit reports what followed, and `noteAction` is
     defined below it. A ref is the join, rather than reordering the file around
     one call. */
  const noteActionRef = useRef<AssistantApi["noteAction"] | null>(null);
  /* Set when the deployment answered 501 to a conversation: it holds no
     conversational model, so she stops asking until the page reloads. */
  const agentOff = useRef(false);
  /* Said once per page when the conversation fails for another reason, so a
     fallback answer is never mistaken for her holding the conversation. */
  const agentFailSaid = useRef(false);
  /*
   * Told to stop. Set when she is interrupted or signed off, and cleared only
   * when the person turns to her again — her name, the microphone, or a
   * question typed. While it is set nothing opens her microphone by itself:
   * the loops that reopened it the moment a conversation ended are what
   * brought her back seconds after "stop".
   */
  const stoodDown = useRef(false);
  const [onCall, setOnCall] = useState(false);
  const onCallRef = useRef(false);
  useEffect(() => {
    onCallRef.current = onCall;
  }, [onCall]);
  /* The wake watcher greets and then takes an instruction, and both are defined
     below it. A ref is the join. */
  const startListeningRef = useRef<(() => void) | null>(null);
  const greetThenListenRef = useRef<(() => void) | null>(null);
  /* For "Sky, what is open today" said in one breath: the question goes straight in. */
  const askRef = useRef<((q: string) => Promise<void>) | null>(null);

  /*
   * Everything below is keyed to the signed-in principal. Two people share a
   * workstation; the night shift should not inherit the day shift's voice.
   */
  const who = principalKey(session.principal);
  /*
   * The level in force, the screen in front of the person, and where they go.
   * The level is read through a ref inside `ask` so a change in Settings
   * applies to the next question without `ask` being rebuilt.
   */
  const level = useAccessLevel(session.principal, who);
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);
  const navigate = useNavigate();
  const [screen, setScreen] = useState<ScreenContext | null>(() => getScreen());
  useEffect(() => subscribeScreen(() => setScreen(getScreen())), []);
  const offered = useRef(new Set<string>());
  useEffect(() => {
    void learning.pull(who, session.source);
  }, [who, session.source]);
  /*
   * Where the person went right after a question she could not answer is
   * the answer they were after: she remembers the screen for that question.
   */
  useEffect(() => {
    const pending = learning.pendingUnanswered(who);
    if (!pending || pathname === pending.route) return;
    const desc = getScreen()?.route === pathname ? getScreen() : describeRoute(pathname);
    if (desc) learning.learnRoute(who, pending.q, pathname, desc.title, session.source);
  }, [pathname, who, session.source]);
  const [prefs, setPrefsState] = useState<Preferences>(() => loadPreferences(who));
  const [reminders, setReminders] = useState<Reminder[]>(() => loadReminders(who));
  const [dueState, setDueState] = useState<DueState>(() => loadDueState(who));
  const [dueItems, setDueItems] = useState<DueItem[]>([]);
  /* The scan runs on a timer and decides whether to bring her back, so it needs
     the current settings without the timer restarting every time one changes. */
  const dueStateRef = useRef(dueState);
  const prefsRef = useRef(prefs);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [present, setPresent] = useState(false);
  /*
   * A microphone the browser will not give her. A sandboxed frame refuses it
   * outright, and the first version treated every refusal as a fresh try: the
   * conversation loop reopened the microphone half a second after each error,
   * each error was written to the log, and the panel filled with the same
   * sentence while the typing box jumped about under it. Now a refusal is said
   * once, stops the loop, and stands until the operator presses the microphone
   * again.
   */
  const [micBlocked, setMicBlocked] = useState<string | null>(null);
  const micReported = useRef(false);
  /* "Thanks" → a goodbye → a slow fade into the corner tab. `leaving` is set by
     the sign-off; the fade itself waits for her goodbye to be out of the air,
     and `withdraw` follows the fade. */
  const [leaving, setLeaving] = useState(false);
  const fading = leaving && !speaking;
  /* Whether the microphone could not be opened at all. An event, not a render
     concern — which is what lets `waking` below be derived rather than stored. */
  const [wakeFailed, setWakeFailed] = useState(false);
  const wake = useRef<ears.WakeWatcher | null>(null);
  /* A microphone that cannot be opened is worth saying once. Saying it on every
     attempt writes a turn, which re-renders, which attempts again. */
  const wakeReported = useRef(false);

  /*
   * Derived, not stored.
   *
   * Whether she is listening for her name is entirely a function of the
   * settings and where she is — storing it meant an effect writing state on
   * every change, which is a cascade waiting to happen and what the linter was
   * right to object to. Not attempted at all where the browser has no
   * recogniser: there is nothing to report to an operator who cannot act on it,
   * and the Voice settings say so in the one place they would look.
   */
  const [conversing, setConversing] = useState(false);
  /*
   * Listening for her name whenever she is not already listening or talking:
   * away in the corner, on screen, after a conversation went quiet, after
   * "stop". It used to run only while she was away, so once she was on the
   * screen and the conversation had lapsed, "hey Sky" reached nobody.
   */
  const waking =
    prefs.voice.wake && ears.isSupported() && !wakeFailed && !prefs.hidden && !listening && !speaking && !conversing;
  const [heard, setHeard] = useState("");
  /* Read inside the recogniser's own callbacks, which outlive the render that
     created them. */
  const conversingRef = useRef(false);
  const silences = useRef(0);
  /* Microphones that closed with nothing heard and no error, in a row. */
  const quietEnds = useRef(0);
  const opening = useRef(false);
  const ear = useRef<ears.Listener | null>(null);

  /*
   * Re-read during render rather than in an effect. Switching account must not
   * leave one frame in which the new operator is looking at the previous one's
   * settings — with an effect, that frame exists.
   */
  const [loadedFor, setLoadedFor] = useState(who);
  if (loadedFor !== who) {
    setLoadedFor(who);
    setPrefsState(loadPreferences(who));
    setReminders(loadReminders(who));
    setDueState(loadDueState(who));
  }

  useEffect(() => () => abort.current?.abort(), []);
  /* Nothing should still be talking after she is unmounted. */
  useEffect(() => () => voice.stop(), []);

  /*
   * Read through a ref so `push` stays stable. Quiet changes what she does with
   * a turn, never whether the turn is written: the log is what an operator
   * reads afterwards to see what they missed while she was silent.
   */
  const quiet = useRef(prefs.quiet);
  useEffect(() => {
    quiet.current = prefs.quiet;
  }, [prefs.quiet]);

  const push = useCallback((turn: Omit<Turn, "id" | "at">) => {
    const full: Turn = { ...turn, id: nextId(), at: Date.now() };
    setTurns((t) => [...t, full]);
    if (turn.role === "assistant" && !quiet.current) {
      setOpen((isOpen) => {
        if (!isOpen) setUnread((u) => u + 1);
        return isOpen;
      });
    }
    return full;
  }, []);

  /*
   * An offer on arrival, once per screen per session: what the screen is and
   * two or three things she can do on it, each one press from an answer. Not
   * when she has been asked to be quiet, and never for a screen above the
   * person's level.
   */
  useEffect(() => {
    if (!screen || screen.route !== routeRef.current) return;
    if (offered.current.has(screen.route)) return;
    if (!canSee(levelRef.current, screen.route)) return;
    if (!prefsRef.current.greet || prefsRef.current.quiet) return;
    offered.current.add(screen.route);
    /* Not in the middle of a conversation: a screen she opened while
       answering spoke over her answer. */
    if (conversingRef.current || onCallRef.current) return;
    push({
      role: "assistant",
      text: arrivalLine(screen),
      suggestions: [
        ...screen.offers.slice(0, 3).map((o, i) => ({ id: `offer-${screen.route}-${i}`, label: o.label, ask: o.ask })),
        { id: `offer-${screen.route}-tour`, label: "Show me around this screen", ask: "show me around this screen" },
      ],
    });
  }, [screen, push]);

  /* ── walkthroughs: she shows, on the page, rather than only tells ─────── */

  const { layout } = useNavLayout();
  const layoutRef = useRef(layout);
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);
  /** The last review, so "walk me through them" and "show me item 2" mean that one. */
  const lastReview = useRef<Review | null>(null);

  /* A walkthrough speaks each step unless she has been asked to be quiet — a
     tour read silently is still a tour, with the caption and a reading time. */
  const stageSpeak = useCallback((text: string): Promise<void> | null => {
    if (prefsRef.current.quiet) return null;
    let done: () => void = () => {};
    const finished = new Promise<void>((resolve) => (done = resolve));
    nowSpeaking.current = text;
    const ok = voice.speak(text, {
      voiceURI: prefsRef.current.voice.voiceURI || undefined,
      rate: prefsRef.current.voice.rate,
      pitch: prefsRef.current.voice.pitch,
      onStart: () => setSpeaking(true),
      onEnd: () => {
        nowSpeaking.current = "";
        setSpeaking(false);
        done();
      },
    });
    if (!ok) {
      nowSpeaking.current = "";
      return null;
    }
    return finished;
  }, []);

  const walk = useCallback(
    (id: string, title: string, steps: stage.Step[], after?: Suggestion[]) => {
      if (steps.length === 0) return false;
      /* the conversation folds away so the page is visible; it comes back at the end */
      setOpen(false);
      void stage.play(
        {
          id,
          title,
          steps,
          onEnd: (how) => {
            if (!after) return;
            push({
              role: "assistant",
              text: how === "finished" ? `That was ${title.toLowerCase()}. What next?` : "Stopped there. Ask again whenever you want to carry on.",
              suggestions: after,
            });
            setOpen(true);
          },
        },
        {
          navigate: (r) => navigate(r),
          route: () => routeRef.current,
          speak: stageSpeak,
          silence: () => {
            voice.stop();
            nowSpeaking.current = "";
            setSpeaking(false);
          },
        },
      );
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [push, stageSpeak],
  );

  const reviewNow = useCallback(async (signal?: AbortSignal): Promise<Review> => {
    let r = await reviewSystem(session.source, session.site.id, session.principal, levelRef.current);
    r = await orderWithModel(r, configRef.current, signal);
    lastReview.current = r;
    return r;
  }, [session.source, session.site.id, session.principal]);

  /*
   * Speaking up, unasked — twice, and only within what quiet allows.
   *
   * Once per sign-in, ever: the offer of a tour, a few seconds after the
   * welcome. And a review in the background — shortly after arrival, then every
   * ten minutes — that raises a critical item the first time it appears, with
   * the offer to walk to it. Never while a walkthrough is running, never when
   * she has been asked to be quiet, and never the same item twice in a session.
   */
  useEffect(() => {
    let key = "";
    try {
      key = `sky.tour-offered.${who}`;
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    const timer = window.setTimeout(() => {
      if (prefsRef.current.quiet || !prefsRef.current.greet || stage.isRunning()) return;
      try {
        localStorage.setItem(key, "1");
      } catch {
        /* storage off: offered again next time, which is harmless */
      }
      push({
        role: "assistant",
        text: "New here? I can walk you around the platform — I point at each part as I go, and you can stop me any time.",
        suggestions: [
          { id: "first-tour", label: "Give me the tour", ask: "give me a tour" },
          { id: "first-screen", label: "Show me around this screen", ask: "show me around this screen" },
        ],
      });
    }, 9000);
    return () => window.clearTimeout(timer);
  }, [who, push]);

  const announced = useRef(new Set<string>());
  useEffect(() => {
    let first = true;
    let cancelled = false;
    let later = 0;
    const sweep = async () => {
      if (cancelled || prefsRef.current.quiet || stage.isRunning()) return;
      /* Not in the middle of a conversation: raised at 45 s, it cut off the
         answer she was reading aloud. It waits for a quiet minute instead. */
      if (conversingRef.current || onCallRef.current || nowSpeaking.current) {
        window.clearTimeout(later);
        later = window.setTimeout(() => void sweep(), 60_000);
        return;
      }
      let r: Review;
      try {
        r = await reviewNow();
      } catch {
        return;
      }
      if (cancelled) return;
      const fresh = r.findings.filter((f) => f.weight === "critical" && !announced.current.has(f.id));
      fresh.forEach((f) => announced.current.add(f.id));
      const wasFirst = first;
      first = false;
      if (fresh.length === 0 || prefsRef.current.quiet) return;
      const walkIt: Suggestion[] = [
        { id: "pro-walk", label: "Walk me through them", ask: "walk me through the critical items" },
        { id: "pro-list", label: "List them", ask: "review the critical items across the system" },
      ];
      push({
        role: "assistant",
        text: wasFirst
          ? `I reviewed the system: ${fresh.length} critical item${fresh.length === 1 ? "" : "s"} across alerts, work orders and what is coming due. Want me to take you through them?`
          : `Something new since I last looked: ${fresh[0].label} — ${fresh[0].because}.${fresh.length > 1 ? ` And ${fresh.length - 1} more.` : ""}`,
        suggestions: walkIt,
      });
    };
    const start = window.setTimeout(() => void sweep(), 45_000);
    const every = window.setInterval(() => void sweep(), 10 * 60_000);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
      window.clearTimeout(later);
      window.clearInterval(every);
    };
  }, [reviewNow, push]);

  const findingStep = (f: Finding, i: number, r: Review): stage.Step => ({
    title: `${i + 1}. ${f.weight === "critical" ? "Critical" : f.weight === "high" ? "High" : "Watch"}`,
    route: f.href,
    target: f.find ? { text: f.find } : { sel: "[data-sky='main']" },
    fallback: { sel: "[data-sky='main']" },
    text: [
      `${f.label}.`,
      f.because.replace(/ · /g, ", ") + ".",
      r.ordered?.why[f.id] ? r.ordered.why[f.id] : "",
      `One option: ${f.task}. ${f.tradeOff}`,
    ]
      .filter(Boolean)
      .join(" "),
  });

  const setConfig = useCallback((c: ExternalConfig) => {
    setConfigState(c);
    saveConfig(c);
  }, []);

  /* Closing her ends the conversation with it: a microphone left listening
     behind a closed panel is the worst of both. */
  const dismiss = useCallback(() => {
    setOpen(false);
    setConversing(false);
  }, []);

  /* A work order the model prepared, as the card the person presses to raise it. */
  const workOrderDraft = useCallback(
    (d: NonNullable<SkyConverseReply["draft"]>) => ({
      id: `wo-${Date.now()}`,
      kind: "Work order",
      title: d.title,
      summary: [
        `Priority P${d.priority} · ${d.kind}`,
        ...(d.assetTag ? [`Asset ${d.assetTag}`] : []),
        ...(d.zoneCode ? [`Zone ${d.zoneCode}`] : []),
        ...(d.slaHours ? [`Due within ${d.slaHours} h`] : []),
        ...(d.description ? [d.description] : []),
      ],
      commitLabel: "Review and raise",
      commit: async () => {
        const wo = await session.source.createWorkOrder({
          siteId: session.site.id,
          title: d.title,
          kind: d.kind as WorkOrderKind,
          priority: d.priority as WorkOrderPriority,
          description: d.description,
          assetTag: d.assetTag,
          zoneCode: d.zoneCode,
          slaHours: d.slaHours,
        });
        noteActionRef.current?.(
          { kind: "work-order-created", id: wo.number, priority: `P${wo.priority}`, title: wo.title },
          {},
        );
      },
    }),
    [session.source, session.site.id],
  );

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q) return;
      answerAloud.current = heardIt.current;
      heardIt.current = false;
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;

      /*
       * The platform's own assistant.
       *
       * Every figure Sky states about this estate comes through here: a
       * catalogued query, run as this operator, with the rows attached. It
       * returns a refusal rather than a guess when the catalogue does not
       * cover the question, and `renderReply` returns null for that — so a
       * refusal falls through to whatever Sky can read for herself instead of
       * ending the conversation.
       *
       * The session id is threaded back in so "and last month?" means
       * something. It is deliberately not reset when she is dismissed: an
       * operator who closes the dock mid-thought and reopens it has not
       * changed the subject. The platform expires it after twenty minutes,
       * which is the judgement that actually matters and is not this layer's
       * to second-guess.
       */
      const showMe = async (intent: ShowIntent | null, where: string | null, question: string, signal: AbortSignal): Promise<boolean> => {
        const have = levelRef.current;
        const lay = layoutRef.current;
        const say = (text: string, extra: Partial<Turn> = {}) => {
          setMark("speaking");
          push({ role: "assistant", text, ...extra });
        };
        const more: Suggestion[] = [
          { id: "w-screen", label: "Show me around this screen", ask: "show me around this screen" },
          { id: "w-review", label: "Review the critical items", ask: "review the critical items across the system" },
        ];

        if (where) {
          const here = findByText(where) ?? findWords(where);
          if (here) {
            say(`Here — ${nameOf(here).slice(0, 80) || where}.`);
            return walk("where", "Where it is", [{ title: "Here it is", target: { text: where }, hold: 7000, text: `This is ${nameOf(here).slice(0, 80) || where}.` }]);
          }
          const to = findScreen(lay, have, where);
          if (to) {
            say(`That is on ${labelOf(lay, to)} — taking you there.`);
            return walk("where", "Where it is", screenVisit(lay, to), more);
          }
          return false;
        }
        if (!intent) return false;

        switch (intent.kind) {
          case "stop":
            stage.stop();
            say("Stopped the walkthrough.");
            return true;

          case "site-tour": {
            const steps = siteTour(lay, have, intent.full);
            say(
              `Starting the ${intent.full ? "full" : "quick"} tour — ${steps.length} stops. Next and Back are on the caption; Escape stops it.`,
              { more: { label: "The tour as a list", lines: tour() } },
            );
            try {
              localStorage.setItem(`sky.toured.${who}`, "1");
            } catch {
              /* storage off: she may offer the tour again, which is harmless */
            }
            return walk(
              "site",
              intent.full ? "The full tour" : "The quick tour",
              steps,
              intent.full ? more : [{ id: "w-full", label: "Take the full tour", ask: "give me the full tour" }, ...more],
            );
          }

          case "screen-tour": {
            const steps = screenTour(lay, routeRef.current);
            say("Walking you through this screen.");
            return walk("screen", "This screen", steps, [more[1]]);
          }

          case "named-tour": {
            const suite = findSuite(lay, intent.words);
            if (suite) {
              const steps = suiteTour(lay, have, suite);
              if (steps.length) {
                say(`Walking you through ${steps[0].title} — ${steps.length - 1} screens.`);
                return walk("suite", `The ${steps[0].title} suite`, steps, more);
              }
            }
            const to = findScreen(lay, have, intent.words) ?? findModule(intent.words)?.route ?? null;
            if (to && canSee(have, to)) {
              say("Taking you there and walking you through it.");
              return walk("screen", "A screen", screenVisit(lay, to), more);
            }
            return false;
          }

          case "review":
          case "walk-review":
          case "review-item": {
            if (intent.kind === "review" || !lastReview.current) push({ role: "assistant", text: "Reviewing alerts, work orders and what is coming due across the system…" });
            const r = intent.kind !== "review" && lastReview.current ? lastReview.current : await reviewNow(signal);
            if (signal.aborted) return true;
            if (intent.kind === "review-item") {
              const f = r.findings[intent.n - 1];
              if (!f) {
                say(`There is no item ${intent.n} — the review found ${r.findings.length}.`);
                return true;
              }
              say(`Taking you to item ${intent.n}.`);
              return walk("finding", "One finding", [findingStep(f, intent.n - 1, r)]);
            }
            if (intent.kind === "walk-review") {
              if (r.findings.length === 0) {
                say(reviewLines(r, session.site.code).join("\n"));
                return true;
              }
              const top = r.findings.slice(0, 6);
              say(`Walking you through the ${top.length} most pressing, one screen at a time.`);
              return walk("review", "The critical items", top.map((f, i) => findingStep(f, i, r)), [more[0]]);
            }
            say(reviewLines(r, session.site.code).join("\n"), {
              urgent: r.findings.slice(0, 6).map((f, i) => ({
                id: f.id,
                label: `${i + 1}. ${f.label}`,
                because: `${f.weight === "critical" ? "Critical" : f.weight === "high" ? "High" : "Watch"} · ${f.because}${r.ordered?.why[f.id] ? ` — ${r.ordered.why[f.id]}` : ""}`,
                rank: i,
                href: f.href,
                options: [{ id: `${f.id}-task`, label: f.task, tradeOff: f.tradeOff }],
              })),
              suggestions:
                r.findings.length > 0
                  ? [
                      { id: "rv-walk", label: "Walk me through them", ask: "walk me through the critical items" },
                      { id: "rv-1", label: "Show me item 1", ask: "show me item 1" },
                    ]
                  : undefined,
              more: r.findings.length > 6 ? { label: `The other ${r.findings.length - 6}`, lines: r.findings.slice(6).map((f) => `${f.label} — ${f.because}`) } : undefined,
              sources: [
                { kind: "local", label: `Review · ${session.site.code}`, detail: r.checked.join(", ") },
                ...(r.ordered ? [{ kind: "external" as const, label: `Ordered by ${r.ordered.by}`, detail: "every figure checked against the records" }] : []),
              ],
            });
            return true;
          }

          case "how-to": {
            const planned = await planWithModel(configRef.current, question, lay, have, routeRef.current, signal);
            if (signal.aborted) return true;
            if (planned) {
              say(`Here is how — I will show you. (Planned by ${planned.model}; I only point at what is really on the screen.)`);
              return walk("how", "How to", planned.steps, more);
            }
            const to = findScreen(lay, have, intent.goal) ?? findModule(intent.goal)?.route ?? null;
            if (to && canSee(have, to)) {
              say("That is done here — I will take you there and walk you through the screen.");
              return walk("how", "How to", screenVisit(lay, to), more);
            }
            return false;
          }
        }
        return false;
      };

      const askPlatform = async () => {
        try {
          const reply = await session.source.askAssistant({
            question: q,
            siteId: session.site.id,
            sessionId: sessionRef.current,
          });
          if (reply.sessionId) sessionRef.current = reply.sessionId;
          return reply;
        } catch {
          /* No platform, no permission, or it is down. Not said here: the
             caller has other things to try, and an operator does not need to
             hear about an endpoint before they have heard a wrong answer. */
          return null;
        }
      };

      /*
       * An answer in her own words, when a model is connected.
       *
       * Every branch above and below still finds the facts: the catalogue,
       * the screen's engine, the briefing. What changes is who says them.
       * With a model connected she answers as someone in the conversation —
       * the model is handed what was found and what has been said so far,
       * and words the reply. The facts are its only material: a reply naming
       * a figure the material, the question and the conversation do not
       * contain is dropped, and so is one that fails or takes too long; the
       * answer then goes out exactly as the branch wrote it. Nothing here can
       * add a fact, only lose the model's phrasing of one.
       *
       * Without a model this is `push`, so a deployment with no key behaves
       * exactly as it did.
       */
      /* The conversation before this question, for the model to follow. */
      const conversation = (): HistoryTurn[] => {
        const said = turnsRef.current.slice(-9);
        /* this question is already in the log; it is sent as the question */
        if (said.length && said[said.length - 1].role === "operator" && said[said.length - 1].text === q) said.pop();
        return said.slice(-8).map((t) => ({ role: t.role === "operator" ? "user" : "assistant", content: t.text }));
      };

      const inHerWords = (turn: Omit<Turn, "id" | "at">) => {
        const cfg = configRef.current;
        if (!hasModel(cfg) || !turn.text.trim()) {
          push(turn);
          return;
        }
        setMark("thinking");
        void (async () => {
          const material = [turn.text, ...(turn.more?.lines ?? [])].join("\n");
          const history = conversation();
          try {
            const reply = await askModel(
              cfg,
              CONVERSE_PROMPT,
              `Question: ${q}\n\nWhat the platform found — the only facts you may use:\n${material}`,
              AbortSignal.any([ctrl.signal, AbortSignal.timeout(COMPOSE_TIMEOUT_MS)]),
              history,
            );
            if (ctrl.signal.aborted) return;
            const allowed = figuresIn(material, q, ...history.map((h) => h.content));
            if (reply.text && verifyFigures(reply.text, allowed).length === 0) {
              setMark("speaking");
              push({
                ...turn,
                text: reply.text,
                spoken: undefined,
                more: { label: turn.more?.label ?? "What the records say", lines: [...turn.text.split("\n"), ...(turn.more?.lines ?? [])] },
                sources: [...(turn.sources ?? []), { kind: "external", label: reply.model, detail: "worded by the model from the records above, every figure checked" }],
              });
              window.setTimeout(() => setMark("idle"), 900);
              return;
            }
          } catch {
            /* unreachable, refused or slow: the answer goes out as written */
          }
          if (ctrl.signal.aborted) return;
          setMark("speaking");
          push(turn);
          window.setTimeout(() => setMark("idle"), 900);
        })();
      };

      const speakReply = (reply: NonNullable<Awaited<ReturnType<typeof askPlatform>>>) => {
        const shown = renderReply(reply, session.site.code);
        if (!shown) return false;
        learning.recordAsk(who, q, "platform", routeRef.current, session.source);
        setMark("speaking");
        const turn = { role: "assistant" as const, text: shown.text, spoken: shown.spoken, sources: shown.sources, more: shown.more };
        /* A refusal is the platform declining on purpose; it is said in the
           platform's words, never softened into a conversation. */
        if (reply.refused) push(turn);
        else inHerWords(turn);
        window.setTimeout(() => setMark("idle"), 900);
        return true;
      };

      /*
       * A follow-up goes to the platform first, ahead of the screen readings.
       *
       * "And last month?" is not a question about the page in front of the
       * operator; it is the previous question with one thing changed, and only
       * the side holding that previous question can resolve it. Routing it
       * through `pageTopic` would answer a question nobody asked, in a
       * confident voice, which is the failure this whole module is arranged
       * around.
       */
      /*
       * Whether the deployment's model holds the conversation: whenever the
       * platform holds one, whatever Sky's settings name. The setting chooses
       * which model words her own readings; it must not switch off the one
       * path that can look anything up. It did, and a person who had pasted a
       * key of their own never reached the conversation at all.
       */
      const agentReady = !agentOff.current && session.source.kind === "platform" && platformModel() !== null;

      stoodDown.current = false;

      if (!agentReady && sessionRef.current && looksLikeFollowUp(q)) {
        const reply = await askPlatform();
        if (ctrl.signal.aborted) return;
        if (reply && speakReply(reply)) return;
      }

      push({ role: "operator", text: q });
      setOpen(true);
      setUnread(0);
      setMark("thinking");

      const have = levelRef.current;
      const scr = getScreen();
      const record = (outcome: learning.Outcome) => learning.recordAsk(who, q, outcome, routeRef.current, session.source);
      const offersOf = (c: ScreenContext) => c.offers.slice(0, 3).map((o, i) => ({ id: `offer-${i}`, label: o.label, ask: o.ask }));

      /*
       * Access before anything else.
       *
       * A question above the person's level is refused by its topic, whatever
       * screen it is asked on, and the refusal names the level and never the
       * content: what the chief executive is deciding is not something an
       * operations sign-in hears about, and neither is what is on the screens
       * that hold it.
       */
      const need = topicLevel(q);
      const aboveByScreen = scr && !canSee(have, scr.route) && isAboutScreen(q);
      if (!atLeast(have, need) || aboveByScreen) {
        push({ role: "assistant", text: refusalFor(have, !atLeast(have, need) ? need : screenLevel(scr!.route)) });
        record("refused");
        setMark("idle");
        return;
      }

      if (learning.isLearningQuestion(q)) {
        setMark("speaking");
        push({ role: "assistant", text: learning.learningLines(who).join("\n") });
        record("guide");
        window.setTimeout(() => setMark("idle"), 700);
        return;
      }

      /*
       * Show, not tell: a tour, a walk through this screen, the critical items
       * across the system, "where is …", "show me how to …". Ahead of the
       * screen and the catalogue because these are asking her to move the
       * page, and none of them is a question about the records. Anything she
       * cannot place falls through to the ordinary answers below.
       */
      const intent = showIntent(q);
      const where = intent ? null : whereIsTarget(q);
      if (intent || where) {
        const shown = await showMe(intent, where, q, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (shown) {
          record("guide");
          window.setTimeout(() => setMark("idle"), 700);
          return;
        }
      }

      /* A screen she was taught for this question: open it and say why. */
      const taught = learning.taughtRoute(who, q);
      if (taught && taught.route !== routeRef.current && canSee(have, taught.route)) {
        setMark("speaking");
        push({
          role: "assistant",
          text: `Last time you asked that, ${taught.title} was where the answer was — opening it.`,
          suggestions: [{ id: "taught", label: `Open ${taught.title}`, href: taught.route }],
        });
        record("taught");
        navigate(taught.route);
        window.setTimeout(() => setMark("idle"), 700);
        return;
      }

      /*
       * Sky in conversation: the deployment's model holds it.
       *
       * The model chooses which of the platform's lookups to run, and the
       * platform runs them as this person and checks every figure in the
       * reply. A work order comes back as a draft for the person to press;
       * a screen comes back for the console to open, and only if this person
       * may see it. Everything above — the level refusal, the tours and
       * walk-throughs — stays in front of it, and "stop listening" is never
       * a conversation. If the deployment has no conversational model, or
       * the call fails, the readings below answer exactly as before.
       */
      if (agentReady && !isSignOff(q)) {
        setMark("thinking");
        /* A reply that needs several lookups takes a while; said aloud, the
           wait is not silence. Cut off by the answer the moment it arrives. */
        const holding =
          answerAloud.current || prefsRef.current.voice.enabled
            ? window.setTimeout(() => {
                voice.speak("One moment, let me check.", {
                  voiceURI: prefsRef.current.voice.voiceURI || undefined,
                  rate: prefsRef.current.voice.rate,
                  pitch: prefsRef.current.voice.pitch,
                });
              }, 3000)
            : 0;
        try {
          const reply = await session.source.converseWithSky(
            {
              message: q,
              siteId: session.site.id,
              sessionId: sessionRef.current,
              localTime: new Date().toLocaleString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }),
              history: conversation(),
              screen:
                scr && canSee(have, scr.route)
                  ? { route: scr.route, title: scr.title, summary: scr.summary, material: scr.material }
                  : undefined,
              screens: screenList(layoutRef.current, have),
            },
            ctrl.signal,
          );
          window.clearTimeout(holding);
          if (ctrl.signal.aborted) return;
          if (reply.sessionId) sessionRef.current = reply.sessionId;
          record("model");
          setMark("speaking");
          push({
            role: "assistant",
            text: reply.text,
            sources: agentSources(reply),
            draft: reply.draft ? workOrderDraft(reply.draft) : undefined,
          });
          if (reply.navigate && canSee(have, reply.navigate)) navigate(reply.navigate);
          window.setTimeout(() => setMark("idle"), 900);
          return;
        } catch (e) {
          window.clearTimeout(holding);
          if (ctrl.signal.aborted) return;
          if (e instanceof DataSourceError && e.status === 501) agentOff.current = true;
          else if (!agentFailSaid.current) {
            agentFailSaid.current = true;
            const why = e instanceof Error ? e.message.replace(/\s+/g, " ").slice(0, 220) : String(e);
            push({
              role: "assistant",
              text: `I could not reach my conversation model just now, so this answer comes from my built-in readings and I will not remember the conversation. The platform said: ${why}`,
            });
          }
          setMark("thinking");
          /* the readings below answer instead */
        }
      }

      /*
       * The screen in front of the person, when it has said what it shows.
       *
       * "What am I looking at" is answered from the screen's own summary; a
       * question the screen's engine can answer is answered by it, before
       * the platform and long before any search. A screen that published
       * nothing is described from the guide instead of being guessed at.
       */
      if (scr && canSee(have, scr.route)) {
        if (isAboutScreen(q)) {
          setMark("speaking");
          inHerWords({
            role: "assistant",
            text: [arrivalLine(scr), ...scr.summary.slice(1)].join("\n"),
            suggestions: offersOf(scr),
            sources: [{ kind: "local", label: scr.title, detail: "what the screen itself computed" }],
          });
          record("screen");
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }
        const own = scr.own?.(q);
        if (own) {
          setMark("speaking");
          inHerWords({
            role: "assistant",
            text: own,
            suggestions: offersOf(scr).slice(0, 2),
            sources: [{ kind: "local", label: scr.title, detail: "the screen's own engine" }],
          });
          record("screen");
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }
      }

      /*
       * Where the question lives, when it is not here.
       *
       * "What is the CEO deciding" asked on the Cash desk belongs to the
       * Decision Room; "when do we run out of stands" belongs to Capacity &
       * Expansion. She says which screen, opens it, and the screen's own
       * engine answers there — rather than a search of work-order titles
       * answering a question that was never about work orders.
       */
      const home = routeForTopic(q);
      if (home && home.route !== routeRef.current.split("?")[0] && canSee(have, home.route) && !isAboutScreen(q)) {
        setMark("speaking");
        push({
          role: "assistant",
          text: `That lives on ${home.title}. Opening it — ask me there and I will answer from its own figures.`,
          suggestions: [{ id: "home", label: `Open ${home.title}`, href: home.route }],
        });
        record("guide");
        navigate(home.route);
        window.setTimeout(() => setMark("idle"), 700);
        return;
      }

      if (!scr && isAboutScreen(q)) {
        const d = describeRoute(routeRef.current);
        if (d) {
          setMark("speaking");
          push({
            role: "assistant",
            text: [`You are on ${d.title}.`, ...d.summary].join("\n"),
            suggestions: d.offers.map((o, i) => ({ id: `offer-${i}`, label: o.label, ask: o.ask })),
            sources: [{ kind: "local", label: "Sky's guide to this platform", detail: d.title }],
          });
          record("guide");
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }
      }

      /*
       * "Hmm." — a beat of thought, aloud, when she is going to answer aloud.
       * Not written to the log: a transcript full of hums is noise. If the
       * answer comes back before the hum is over, the answer cuts it short,
       * which is what a quick answer sounds like.
       */
      if (answerAloud.current || prefsRef.current.voice.enabled) {
        voice.speak("Hmm.", {
          voiceURI: prefsRef.current.voice.voiceURI || undefined,
          rate: prefsRef.current.voice.rate,
          pitch: prefsRef.current.voice.pitch,
        });
      }

      /* "Okay, let me search my database." — once per question, before the
         first lookup that leaves the screen, so a wait is never silence. */
      let acknowledged = false;
      const lookingUp = () => {
        if (acknowledged) return;
        acknowledged = true;
        push({ role: "assistant", text: acknowledgement() });
      };

      try {
        /*
         * "Where do I start?" is not a search. Running it through the keyword
         * index answers "nothing matches that", which is true of the words and
         * useless about the airfield — so it is routed to the ordering instead.
         */
        if (isTriageQuestion(q)) {
          const u = await urgent(session.source, session.site.id, session.site.code);
          if (ctrl.signal.aborted) return;
          setMark("speaking");
          inHerWords({ role: "assistant", text: u.lines.join("\n"), urgent: u.items, sources: u.sources });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        /*
         * The screen first.
         *
         * Before the guide and long before the record search: an operator
         * standing on the friction review asking about the condition of the
         * runway is asking about the survey in front of them. Answering that
         * with a keyword search of work-order titles is a search engine being
         * handed a question that wanted a reading.
         */
        const topic = pageTopic(routeRef.current, q);
        if (topic) {
          const answer = await answerForTopic(topic);
          if (ctrl.signal.aborted) return;
          if (answer) {
            setMark("speaking");
            inHerWords({
              role: "assistant",
              text: answer.brief.join("\n"),
              suggestions: answer.suggestions,
              more: { label: "Pull the full report", lines: answer.full },
              sources: [{ kind: "local", label: answer.sourceLabel, detail: answer.sourceDetail }],
            });
            window.setTimeout(() => setMark("idle"), 900);
            return;
          }
          /* The dataset would not load. Said plainly rather than falling through
             to a search that answers a different question. */
          push({
            role: "assistant",
            text: `I could not read the ${topic} data for this screen, so I have nothing to tell you about it. I have not searched the records instead — that would answer a different question.`,
          });
          setMark("idle");
          return;
        }

        /* Said back, not searched for. */
        if (isGreeting(q)) {
          setMark("speaking");
          inHerWords({ role: "assistant", text: whatWouldYouLike(firstName(session.principal.name)).join("\n") });
          window.setTimeout(() => setMark("idle"), 700);
          return;
        }

        /* An instruction to stop, which a live microphone has to obey. */
        if (isSignOff(q)) {
          ears.voiceLog(`sky: signed off by "${q}"`);
          stoodDown.current = true;
          setConversing(false);
          setMark("speaking");
          push({ role: "assistant", text: "Right you are. Say my name when you need me." });
          window.setTimeout(() => setMark("idle"), 700);
          /* and then she goes: a slow fade into the corner once the goodbye is
             said — or after four seconds regardless, because a speech engine
             that never reports the end of an utterance would otherwise keep
             her on screen forever, and that has been seen. */
          setLeaving(true);
          window.setTimeout(() => {
            voice.stop();
            setSpeaking(false);
          }, 4000);
          return;
        }

        /*
         * A department, not a screen.
         *
         * "How are operations running" crosses work, alerts and inspections;
         * "what is running late" crosses the portfolio. Neither is answerable
         * from the page in front of the operator, and both are what somebody
         * actually asks an assistant for.
         */
        /*
         * A briefing, only when one was asked for.
         *
         * This used to fire on a department plus any of "issues", "late",
         * "budget" or "risk" — so "is any project over budget", which is a
         * specific question with an exact answer in the projects table, was
         * answered with a cross-department briefing and never reached the
         * catalogue. The catalogue's answer is the better one there: it knows
         * that both Madinah schemes have no spend recorded at all, which is
         * not the same as being inside budget.
         *
         * The looser test still runs, below the platform, so a department
         * question the catalogue cannot take is a briefing rather than a
         * keyword search.
         */
        const dept = departmentIn(q);
        const speakBriefing = async (which: NonNullable<ReturnType<typeof departmentIn>>) => {
          /* A short acknowledgement first: a lookup that crosses three
             endpoints takes a moment, and silence reads as being ignored. */
          lookingUp();
          const brief = await briefingFor(which, {
            source: session.source,
            siteId: session.site.id,
            siteCode: session.site.code,
            principal: session.principal,
            now: Date.now(),
          });
          if (ctrl.signal.aborted) return true;
          if (brief) {
            record("briefing");
            setMark("speaking");
            inHerWords({
              role: "assistant",
              text: brief.brief.join("\n"),
              suggestions: brief.suggestions,
              more: { label: "Pull the full report", lines: brief.full },
              sources: [{ kind: "local", label: brief.sourceLabel, detail: brief.sourceDetail }],
            });
            window.setTimeout(() => setMark("idle"), 900);
            return true;
          }
          push({
            role: "assistant",
            text: `I could not put a ${which} briefing together — either your sign-in does not reach those records, or there are none at this site.`,
          });
          setMark("idle");
          return true;
        };
        if (dept && isBriefingRequest(q)) {
          if (await speakBriefing(dept)) return;
        }

        /*
         * "What does ICAO require here?"
         *
         * Answered from the citations rather than from the data, and before the
         * readings, because the two are different questions — one asks what the
         * runway measured, the other what the rule expects. She names the
         * document and says plainly that she is quoting a reference rather than
         * being the authority.
         */
        if (isStandardsQuestion(q)) {
          const section = sectionFor(routeRef.current) ?? { id: pageTopic(routeRef.current, "condition") ?? "" };
          const cited = standardsAnswer(section.id);
          if (cited) {
            const outside = hasModel(config)
              ? "A model is connected, so I can go and read the current text if you ask me to."
              : "Nothing is connected to the outside, so this is what I carry, not the current published text. Connect a model and a search key under Sources and I can go and read it.";
            setMark("speaking");
            inHerWords({
              role: "assistant",
              text: [...cited.brief, "", outside].join("\n"),
              more: { label: "Pull the full citations", lines: cited.full },
              sources: [{ kind: "local", label: "Regulatory reference", detail: section.id }],
            });
            window.setTimeout(() => setMark("idle"), 900);
            return;
          }
          push({
            role: "assistant",
            text: `I have no citation for this screen that I am confident in, and I will not guess at a regulation. ${CURRENCY}`,
          });
          setMark("idle");
          return;
        }

        /*
         * Every other screen with numbers on it.
         *
         * Same shape as friction and rubber: count what the page draws, say
         * what it implies, offer what could be done. A screen that is a drawing
         * rather than a dataset returns nothing here and falls through to the
         * guide, which explains it — inventing a statistic for the aerodrome
         * plan would be worse than saying there is nothing to count.
         */
        /*
         * The catalogue, before the screen.
         *
         * `pageTopic` above has already taken the questions that are really
         * about the screen — the friction survey, the rubber readings — by
         * matching the question against the topic as well as the route. What
         * is left here is the broad fallback: any screen with numbers on it
         * answers any question shaped like a reading, and its word list
         * includes "how many", "anything" and "right now".
         *
         * That is how "how many things are broken on the airfield right now"
         * came back as commentary on the KPI panel that happened to be on
         * screen — a question about the estate answered by the furniture in
         * front of the operator, which is the complaint this whole module
         * exists to answer. The platform understands the question and holds
         * the estate; it gets first refusal, and what it refuses still falls
         * through to the screen reading below, unchanged.
         */
        lookingUp();
        const platform = await askPlatform();
        if (ctrl.signal.aborted) return;
        if (platform && speakReply(platform)) return;

        /*
         * A department question the catalogue could not take.
         *
         * "What is late in engineering" names a department and asks something
         * specific, and the catalogue has no engineering intent — so rather
         * than dropping to a keyword search of record titles, it gets the
         * cross-department briefing it would have got before the platform was
         * wired in at all.
         */
        if (dept && /\b(issues?|problems?|late|budget|risk)\b/i.test(q)) {
          if (await speakBriefing(dept)) return;
        }

        if (!isTourRequest(q) && sectionFor(routeRef.current)) {
          /*
           * Asking what a thing *is* and asking how it *stands* are different
           * questions, and the words that separate them are the state words —
           * not "what is". "What is compliance" wants the explanation; "what is
           * the state of compliance" wants the reading, and putting "what is" in
           * this list sent both to the same place.
           */
          const wantsReading =
            /\b(condition|state of|status|how is|how are|how many|how much|how bad|anything|worst|worry|attention|looking|right now|today|currently|at the moment|summar|analysis|analyse|analyze|assess|reading|readings|report|say|says)\b/i.test(
              q,
            ) &&
            !/\bwo[\s-]?\d/i.test(q) &&
            /* Same exclusion the page answers make: "what does ICAO say" asks
               what a document requires, not what this screen measured. */
            !isAboutAStandard(q);
          if (wantsReading) {
            const answer = await analyseSection(routeRef.current, {
              source: session.source,
              siteId: session.site.id,
              siteCode: session.site.code,
              principal: session.principal,
              now: Date.now(),
            });
            if (ctrl.signal.aborted) return;
            if (answer) {
              record("section");
              setMark("speaking");
              inHerWords({
                role: "assistant",
                text: answer.brief.join("\n"),
                suggestions: answer.suggestions,
                more: { label: "Pull the full report", lines: answer.full },
                sources: [{ kind: "local", label: answer.sourceLabel, detail: answer.sourceDetail }],
              });
              window.setTimeout(() => setMark("idle"), 900);
              return;
            }
          }
        }

        /*
         * A connected model, on the screen's material and only that.
         *
         * The screen's engine emitted every figure it computed; the model may
         * explain, compare and argue over them, and a reply carrying a figure
         * the screen did not produce is withheld and says so. This runs before
         * the keyword search because a question asked on a desk is a question
         * about the desk, and a search of work-order titles is not an answer to it.
         */
        if (scr && canSee(have, scr.route) && scr.material && hasModel(config)) {
          try {
            lookingUp();
            const reply = await askModel(config, scr.prompt ?? SYSTEM_PROMPT, `Question: ${q}\n\nThe screen's material:\n${scr.material}`, ctrl.signal, conversation());
            if (ctrl.signal.aborted) return;
            const bad = scr.figures ? verifyFigures(reply.text, scr.figures) : [];
            if (reply.text && !bad.length) {
              record("model");
              setMark("speaking");
              push({ role: "assistant", text: reply.text, sources: [{ kind: "external", label: reply.model, detail: `on ${scr.title}'s figures, every one checked` }] });
              window.setTimeout(() => setMark("idle"), 900);
              return;
            }
            if (bad.length) {
              record("none");
              push({ role: "assistant", text: `The model's answer was withheld: it stated ${bad.length === 1 ? "a figure" : "figures"} this screen did not produce (${bad.slice(0, 4).join(", ")}). Ask me for the screen's own reading instead.`, suggestions: offersOf(scr) });
              setMark("idle");
              return;
            }
          } catch {
            /* the model is not reachable; the search below still runs */
          }
        }

        /*
         * Teaching the platform, before anything that searches it.
         *
         * Placed first because "what is compliance" must not become a keyword
         * search for the word compliance — an operator asking what a screen is
         * for and being handed six records that mention it has been answered by
         * a machine that did not understand the question.
         */
        if (isTourRequest(q)) {
          setMark("speaking");
          record("guide");
          push({
            role: "assistant",
            text: tour().join("\n"),
            sources: [{ kind: "local", label: "Sky's guide to this platform", detail: "written from the screens" }],
          });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        const term = isExplainRequest(q) ? findTerm(q) : null;
        const moduleAsked = isExplainRequest(q) || isTourRequest(q) ? findModule(q) : null;
        if (term || moduleAsked) {
          /* A term wins over a module when both match: "what is the grade on the
             dashboard" is a question about the grade. */
          const lines = term
            ? [term.name, "", term.what, "", term.how, "", `Where the rule lives: ${term.source}`]
            : [
                `${moduleAsked!.name} — ${moduleAsked!.route}`,
                "",
                moduleAsked!.what,
                "",
                moduleAsked!.how,
                ...(moduleAsked!.look ? ["", `Worth knowing: ${moduleAsked!.look}`] : []),
              ];
          record("guide");
          setMark("speaking");
          push({
            role: "assistant",
            text: lines.join("\n"),
            sources: [
              { kind: "local", label: "Sky's guide to this platform", detail: term ? term.id : moduleAsked!.id },
            ],
          });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        /*
         * "Raise a work order to sweep A2." She fills the form in and stops;
         * the raising is a button, and the button is the operator's.
         */
        if (isScheduleRequest(q)) {
          const parsed = parseWorkOrder(q);
          if (!parsed) {
            push({
              role: "assistant",
              text: "Tell me what the work is and I will fill it in — “raise a P2 work order to sweep A2”, for example.",
            });
            setMark("idle");
            return;
          }
          setMark("speaking");
          push({
            role: "assistant",
            text: "I have filled this in. Nothing is raised until you press it.",
            draft: {
              id: `wo-${Date.now()}`,
              kind: "Work order",
              title: parsed.input.title,
              summary: describeWorkOrder(parsed),
              commitLabel: "Review and raise",
              commit: async () => {
                const wo = await session.source.createWorkOrder({ ...parsed.input, siteId: session.site.id });
                noteActionRef.current?.(
                  { kind: "work-order-created", id: wo.number, priority: `P${wo.priority}`, title: wo.title },
                  {},
                );
              },
            },
          });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        /*
         * "When can I do rubber removal on RWY 17/35 for four hours." The
         * platform answers this properly — traffic and weather, both halves —
         * so she asks it rather than inventing a window.
         */
        if (isPlanRequest(q)) {
          const parsed = parsePlan(q);
          if ("missing" in parsed) {
            push({
              role: "assistant",
              text:
                "I can ask the platform for a window, but it needs three things and I am missing " +
                `${parsed.missing.length === 1 ? "one" : parsed.missing.length}:\n` +
                parsed.missing.map((m) => `• ${m}`).join("\n"),
            });
            setMark("idle");
            return;
          }
          const now = new Date();
          const to = new Date(now.getTime() + 14 * 86_400_000);
          const answer = await session.source
            .whenToWork({
              siteId: session.site.id,
              ...parsed.plan,
              searchFrom: now.toISOString(),
              searchTo: to.toISOString(),
            })
            .catch((e: unknown) => e as Error);
          if (ctrl.signal.aborted) return;

          const lines: string[] = [];
          if (answer instanceof Error) {
            lines.push(`The platform would not answer that: ${answer.message}`);
          } else if (!answer.known) {
            lines.push(
              `${answer.position} · ${answer.category}: the platform cannot say. ${answer.whyNot ?? "Half the data is missing, which is not the same as there being no window."}`,
            );
          } else if (!answer.found) {
            lines.push(
              `${answer.position} · ${answer.category}: no window of ${answer.hoursNeeded} h in the next 14 days.`,
              answer.whyNot ?? "Traffic or weather closes every candidate.",
            );
          } else {
            lines.push(
              `${answer.position} · ${answer.category}, ${answer.hoursNeeded} h:`,
              answer.earliest ? `Earliest: ${answer.earliest.from} → ${answer.earliest.to}` : "",
              answer.windowCount ? `${answer.windowCount} windows in the next 14 days.` : "",
              answer.verdict ?? "",
              answer.caveat ? `Caveat: ${answer.caveat}` : "",
            );
          }
          setMark("speaking");
          push({
            role: "assistant",
            text: lines.filter(Boolean).join("\n"),
            sources: [{ kind: "local", label: `Planning · ${session.site.code}`, detail: "traffic and weather" }],
          });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        /*
         * A named work order is asked about, not searched for. "Status of
         * WO-000012" run through the keyword index returns the order plus
         * everything else that shares a word with its title.
         */
        const ref = workOrderRef(q);
        if (ref && (isStatusQuestion(q) || /^\s*wo/i.test(q))) {
          const st = await statusOf(session.source, session.site.id, session.site.code, ref);
          if (ctrl.signal.aborted) return;
          setMark("speaking");
          push({
            role: "assistant",
            text: st.lines.join("\n"),
            sources: st.sources,
            suggestions: st.options.length ? st.options : undefined,
          });
          window.setTimeout(() => setMark("idle"), 900);
          return;
        }

        const local = await searchLocal(session.source, session.site.id, session.site.code, q, ctrl.signal);
        if (ctrl.signal.aborted) return;

        const sources: Source[] = [...local.sources];
        const lines: string[] = [];

        if (local.findings.length > 0) {
          lines.push(
            `From this platform's records — ${local.findings.length} match${local.findings.length === 1 ? "" : "es"}:`,
          );
          for (const f of local.findings) lines.push(`• ${f.label} — ${f.detail}`);
        } else if (platform?.refused) {
          /* Two different silences, and the operator is owed the difference.
             The search found no matching titles; the platform declined to look
             at all, and it knows what it could have been asked instead. */
          lines.push(refusal(platform), "", `Nothing in ${local.searched} matches those words either.`);
        } else {
          lines.push(`Nothing in ${local.searched} matches that.`);
        }

        // The outside, only when it is actually connected.
        if (hasModel(config)) {
          setMark("thinking");
          let web = "";
          if (hasSearch(config)) {
            try {
              const hits = await searchWeb(config, q, ctrl.signal);
              if (hits.length) {
                web = hits.map((h) => `- ${h.title} (${h.url}): ${h.snippet ?? ""}`).join("\n");
                for (const h of hits.slice(0, 3)) {
                  sources.push({ kind: "external", label: h.title.slice(0, 60), detail: h.url });
                }
              }
            } catch (e) {
              lines.push(e instanceof ExternalUnavailable ? `Search: ${e.message}` : "Search failed.");
            }
          }

          try {
            const context = await briefing(session.source, session.site.id, session.site.code);
            const localText = local.findings.map((f) => `- ${f.label} (${f.detail})`).join("\n");
            const reply = await askModel(
              config,
              SYSTEM_PROMPT,
              [
                `Operator question: ${q}`,
                ``,
                `Platform context: ${context}`,
                localText ? `Matching platform records:\n${localText}` : `No platform records matched.`,
                web ? `Web results:\n${web}` : ``,
              ].join("\n"),
              ctrl.signal,
              conversation(),
            );
            if (ctrl.signal.aborted) return;
            if (reply.text) {
              lines.push("", reply.text);
              sources.push({ kind: "external", label: reply.model, detail: "language model" });
            }
          } catch (e) {
            lines.push("", e instanceof ExternalUnavailable ? e.message : "The model call failed.");
          }
        } else {
          lines.push(
            "",
            "No model is connected, so this is the platform's own records only — not a search of anything outside. You can connect one from the settings button above.",
          );
        }

        record(local.findings.length ? "search" : sources.some((x) => x.kind === "external") ? "model" : "none");
        setMark("speaking");
        push({ role: "assistant", text: lines.join("\n"), sources });
        window.setTimeout(() => setMark("idle"), 900);
      } catch (e) {
        if (ctrl.signal.aborted) return;
        push({
          role: "assistant",
          text: `That did not complete: ${e instanceof Error ? e.message : String(e)}`,
        });
        setMark("idle");
      }
    },
    [config, push, session.principal, session.site.code, session.site.id, session.source, who, navigate, walk, reviewNow, workOrderDraft],
  );

  const summon = useCallback(
    (question?: string) => {
      setPresent(true);
      setOpen(true);
      setUnread(0);
      setMark("listening");
      if (question) void ask(question);
      else window.setTimeout(() => setMark("idle"), 1200);
    },
    [ask],
  );

  const noteAction = useCallback(
    (event: ActionEvent, context?: Partial<ConsequenceContext>) => {
      const consequence = consequenceOf(event, { siteCode: session.site.code, ...context });
      if (!consequence) return; // nothing derivable — say nothing rather than fill the space
      if (!prefs.quiet) setMark("speaking");
      push({
        role: "assistant",
        text: consequence.follows.join("\n"),
        sources: consequence.sources,
        consequence: true,
      });
      if (!prefs.quiet) window.setTimeout(() => setMark("idle"), 1100);
    },
    [prefs.quiet, push, session.site.code],
  );

  useEffect(() => {
    noteActionRef.current = noteAction;
  }, [noteAction]);

  const propose = useCallback(
    (text: string, draft: Draft, suggestions?: Suggestion[]) => {
      push({ role: "assistant", text, draft, suggestions });
      setMark("speaking");
      window.setTimeout(() => setMark("idle"), 900);
    },
    [push],
  );

  const setPrefs = useCallback(
    (p: Preferences) => {
      setPrefsState(p);
      savePreferences(who, p);
      /* Turning the voice off should stop the sentence already in the air,
         not the one after it. */
      if (!p.voice.enabled) {
        voice.stop();
        setSpeaking(false);
      }
      /* Going quiet stops the loop too — quiet means quiet. */
      if (p.quiet) setConversing(false);
    },
    [who],
  );

  /* What she is saying right now, for the interruption watcher to discount. */
  const nowSpeaking = useRef("");

  const readAloud = useCallback(
    (text: string) => {
      nowSpeaking.current = text;
      const started = voice.speak(text, {
        voiceURI: prefs.voice.voiceURI || undefined,
        rate: prefs.voice.rate,
        pitch: prefs.voice.pitch,
        onStart: () => setSpeaking(true),
        onEnd: () => {
          nowSpeaking.current = "";
          setSpeaking(false);
        },
      });
      if (!started) {
        nowSpeaking.current = "";
        setSpeaking(false);
      }
    },
    [prefs.voice.pitch, prefs.voice.rate, prefs.voice.voiceURI],
  );

  const stopSpeaking = useCallback(() => {
    voice.stop();
    setSpeaking(false);
  }, []);

  const writeReminders = useCallback(
    (next: Reminder[]) => {
      setReminders(next);
      saveReminders(who, next);
    },
    [who],
  );

  const addReminder = useCallback(
    (text: string, dueAt: number, about?: Reminder["about"]) => {
      writeReminders(ordered([...reminders, newReminder(text, dueAt, about)]));
    },
    [reminders, writeReminders],
  );

  const completeReminder = useCallback(
    (id: string) => {
      writeReminders(reminders.map((r) => (r.id === id ? { ...r, doneAt: Date.now() } : r)));
    },
    [reminders, writeReminders],
  );

  const removeReminder = useCallback(
    (id: string) => writeReminders(reminders.filter((r) => r.id !== id)),
    [reminders, writeReminders],
  );

  /*
   * Reading aloud as answers arrive, when the operator has asked for that.
   * Driven from the turn list rather than from `push`, so that exactly one
   * thing decides what gets spoken and a turn can never be read twice.
   */
  const spoken = useRef<string | null>(null);
  useEffect(() => {
    if ((!prefs.voice.enabled && !answerAloud.current) || prefs.quiet) return;
    const last = turns[turns.length - 1];
    if (!last || last.role !== "assistant" || spoken.current === last.id) return;
    spoken.current = last.id;
    readAloud(last.spoken ?? last.text);
  }, [prefs.quiet, prefs.voice.enabled, readAloud, turns]);

  const show = useCallback(() => {
    setPresent(true);
  }, []);

  const withdraw = useCallback(() => {
    setPresent(false);
    setOpen(false);
  }, []);

  const stopListening = useCallback(() => {
    ear.current?.stop();
    ear.current = null;
    setListening(false);
    setHeard("");
    /* Pressing Stop ends the conversation, not just this turn — otherwise the
       loop reopens the microphone the operator has just closed. */
    stoodDown.current = true;
    setConversing(false);
  }, []);

  /*
   * She takes one instruction at a time and then stops. Continuous listening in
   * a room with radios in it transcribes the room — and an assistant that has
   * been quietly recording all shift is a different product from this one.
   */
  const startListening = useCallback(() => {
    if (ear.current || opening.current) {
      ears.voiceLog("sky: listen asked, but a question-mic is already open");
      return;
    }
    /* Stopping her voice below can fire its end at once, and that end may
       ask for the microphone again before this one exists: two microphones
       on one voice, each aborting the other. */
    opening.current = true;
    /* A press is a retry; the loops below stay off until it succeeds. */
    setMicBlocked(null);
    /* Her own voice would be the loudest thing in the room. */
    voice.stop();
    setSpeaking(false);
    /* and the name-listener off first: two recognisers on one microphone
       fight, and the second silently loses */
    wake.current?.stop();
    wake.current = null;
    setHeard("");
    setListening(true);
    setMark("listening");
    setPresent(true);
    setOpen(true);
    setConversing(true);

    let me: ears.Listener | null = null;
    me = ears.listen(
      {
        onInterim: (t) => setHeard(t),
        /*
         * Closed with nothing heard and nothing wrong — Safari does this to a
         * microphone opened just after her own voice or just after the
         * name-listener closed. Left alone, she sat showing "listening" and
         * heard nothing. During a call she opens it again, a few times.
         */
        onEnd: () => {
          if (ear.current !== me) return;
          ear.current = null;
          setListening(false);
          setHeard("");
          if (conversingRef.current && !stoodDown.current && quietEnds.current < 5) {
            quietEnds.current += 1;
            ears.voiceLog(`sky: question-mic closed quietly, reopening (${quietEnds.current}/5)`);
            window.setTimeout(() => startListeningRef.current?.(), 700);
          } else setMark("idle");
        },
        onFinal: (t) => {
          ear.current = null;
          quietEnds.current = 0;
          silences.current = 0;
          setListening(false);
          setHeard("");
          /* Her name said to a microphone already listening is "are you
             there", not a question for the model. */
          const q = t ? withoutHerName(t) : "";
          if (t && !q) {
            ears.voiceLog(`sky: heard only her name ("${t}")`);
            push({ role: "assistant", text: "Yes — I am listening." });
            setMark("speaking");
            voice.speak("Yes, I am listening.", {
              voiceURI: prefsRef.current.voice.voiceURI || undefined,
              rate: prefsRef.current.voice.rate,
              pitch: prefsRef.current.voice.pitch,
              onEnd: () => {
                setMark("idle");
                startListeningRef.current?.();
              },
            });
          } else if (q) {
            heardIt.current = true;
            void ask(q);
          } else setMark("idle");
        },
        onError: (message) => {
          ear.current = null;
          setListening(false);
          setHeard("");
          setMark("idle");
          /*
           * A quiet room is not a fault.
           *
           * "Nothing was heard" is what a recogniser says every time the operator
           * pauses to think, and writing that into the conversation as a reply
           * would fill the panel with her complaining about silence. During a
           * conversation it simply listens again — but not forever: three in a
           * row means nobody is there, and the microphone closes rather than
           * being left open on an empty room.
           */
          if (/nothing was heard/i.test(message) && conversingRef.current) {
            silences.current += 1;
            if (silences.current < 3) {
              window.setTimeout(() => startListeningRef.current?.(), 600);
            } else {
              silences.current = 0;
              setConversing(false);
              push({ role: "assistant", text: "I have stopped listening — say my name when you need me." });
            }
            return;
          }
          silences.current = 0;
          /*
           * Only a refused or absent microphone blocks her. A network blip or
           * a recogniser that hiccuped is transient: during a conversation she
           * listens again, and otherwise the name-listener carries on. Blocking
           * on every error is what left her deaf after the first answer.
           */
          if (!/refused access|no microphone/i.test(message)) {
            if (conversingRef.current) window.setTimeout(() => startListeningRef.current?.(), 900);
            return;
          }
          setConversing(false);
          setMicBlocked(message);
          if (!micReported.current) {
            micReported.current = true;
            push({
              role: "assistant",
              text: `${message} Type your question instead, or press the microphone to try again.`,
            });
          }
        },
      },
      voice.recognitionLang(prefs.voice.lang, prefs.voice.voiceURI),
    );
    ear.current = me;
    opening.current = false;
    if (!ear.current) {
      setListening(false);
      setMark("idle");
    }
  }, [ask, prefs.voice.lang, prefs.voice.voiceURI, push]);

  /*
   * Listening for her name while she is away.
   *
   * Only while away, and only while not already taking an instruction: two
   * recognisers on one microphone fight, and the second one silently loses.
   * Waking brings her out and starts taking the sentence, so "assistant, what
   * is most urgent" is one continuous act rather than a word and then a wait.
   */
  useEffect(() => {
    if (!waking) {
      wake.current?.stop();
      wake.current = null;
      return;
    }
    if (wake.current) return;

    wake.current = ears.listenForWake(
      ["sky", "hey sky", "assistant"],
      (heard) => {
        wake.current?.stop();
        wake.current = null;
        setPresent(true);
        /* Her name and a question together: answer the question, rather than
           asking what they want and losing what they already said. */
        const rest = afterWakeWord(heard);
        ears.voiceLog(`sky: woke on "${heard}"${rest ? `, asking "${rest}"` : ""}`);
        setOnCall(true);
        if (rest.split(/\s+/).filter(Boolean).length >= 2) {
          stoodDown.current = false;
          setOpen(true);
          setUnread(0);
          /* spoken, so answered aloud and followed by listening for the next */
          heardIt.current = true;
          setConversing(true);
          void askRef.current?.(rest);
          return;
        }
        /*
         * "Hey Sky" gets an answer before it gets a microphone.
         *
         * Waking straight into listening means the operator is already talking
         * into a recogniser that has not started, and the first few words are
         * lost. A short "Hi <name>." both confirms she heard and buys the
         * moment it takes for the second recogniser to come up — and her own
         * voice is off the microphone before it opens, because two recognisers
         * on one microphone fight and the second silently loses.
         */
        greetThenListenRef.current?.();
      },
      (message) => {
        wake.current = null;
        setWakeFailed(true);
        /* Asleep is not deaf: a name-listener that gave up after a run of
           errors tries again shortly, unless the microphone was refused. */
        if (!/refused access|no microphone|no speech recognition/i.test(message)) {
          window.setTimeout(() => setWakeFailed(false), 15_000);
        }
        /* Written to the log rather than shouted, and once: a microphone that
           could not be opened is worth knowing about, not worth interrupting
           for, and certainly not worth repeating. */
        if (!wakeReported.current) {
          wakeReported.current = true;
          push({ role: "assistant", text: message });
        }
      },
      voice.recognitionLang(prefs.voice.lang, prefs.voice.voiceURI),
      true,
    );
    if (wake.current === null) setWakeFailed(true);
  }, [prefs.voice.lang, prefs.voice.voiceURI, push, waking]);

  useEffect(() => () => wake.current?.stop(), []);

  /* On a platform, her voice log goes to its server log by itself. */
  useEffect(() => {
    if (session.source.kind !== "platform") return;
    const source = session.source;
    ears.setVoiceLogSink((lines) => {
      source.sendVoiceLog(lines).catch(() => {});
    });
    return () => ears.setVoiceLogSink(null);
  }, [session.source]);

  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  /* The microphone pressed by the person: that is turning to her again. */
  const listenNow = useCallback(() => {
    stoodDown.current = false;
    startListening();
  }, [startListening]);

  /** Answer to her name, then open the microphone. */
  const greetThenListen = useCallback(() => {
    /* Called by name, she comes back at full size and asks. */
    stoodDown.current = false;
    setOnCall(true);
    const hello = "How can I help you?";
    setOpen(true);
    setUnread(0);
    push({ role: "assistant", text: hello });
    setMark("speaking");
    /* Listen once the hello is out — or after 2.5 s regardless, because
       Safari does not always say that speech has finished, and waiting for
       it left her silent after "How can I help you?". */
    let listened = false;
    const listenNext = () => {
      if (listened) return;
      listened = true;
      setSpeaking(false);
      quietEnds.current = 0;
      startListeningRef.current?.();
    };
    const ok = voice.speak(hello, {
      voiceURI: prefs.voice.voiceURI || undefined,
      rate: prefs.voice.rate,
      pitch: prefs.voice.pitch,
      onStart: () => setSpeaking(true),
      onEnd: listenNext,
    });
    /* No voice on this machine, or speech refused: still open the microphone,
       after a beat, so "Hey Sky" is never a dead end. */
    window.setTimeout(listenNext, ok ? 2500 : 400);
  }, [prefs.voice.pitch, prefs.voice.rate, prefs.voice.voiceURI, push]);

  useEffect(() => {
    greetThenListenRef.current = greetThenListen;
    askRef.current = ask;
  }, [greetThenListen, ask]);

  /*
   * The scan for things running out.
   *
   * Every twenty minutes, and once on arrival. Not faster: nothing here changes
   * on the scale of a minute, and a background poll against the platform that
   * nobody asked for is a cost the operator pays for no gain.
   */
  useEffect(() => {
    let live = true;
    const run = () => {
      scanDue(session.source, session.site.id, session.principal)
        .then((found) => {
          if (!live) return;
          setDueItems(found);
          /*
           * She comes back for these, and only for these.
           *
           * The standing rule is that she does not surface things unbidden
           * mid-task — but a certificate lapsing is exactly the case that rule
           * was costing. So she reappears in the corner with a count, which is
           * the quietest thing that is still noticing: no focus taken, no panel
           * opened, nothing covered. Quiet still means quiet; the count waits on
           * the tab until she is asked.
           */
          const fresh = unseen(found, dueStateRef.current);
          if (fresh.length > 0 && !prefsRef.current.quiet && !prefsRef.current.hidden) setPresent(true);
        })
        .catch(() => live && setDueItems([]));
    };
    run();
    const t = window.setInterval(run, 20 * 60_000);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, [session.principal, session.site.id, session.source]);

  useEffect(() => {
    conversingRef.current = conversing;
  }, [conversing]);

  useEffect(() => {
    dueStateRef.current = dueState;
  }, [dueState]);
  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  const due = useMemo(() => unseen(dueItems, dueState), [dueItems, dueState]);

  const writeDue = useCallback(
    (next: DueState) => {
      setDueState(next);
      saveDueState(who, next);
    },
    [who],
  );

  const ackDue = useCallback(
    (id: string) => {
      const item = dueItems.find((i) => i.id === id);
      if (!item) return;
      writeDue({ ...dueState, acked: { ...dueState.acked, [id]: item.band } });
    },
    [dueItems, dueState, writeDue],
  );

  const stopDue = useCallback(
    (id: string) => writeDue({ ...dueState, stopped: [...new Set([...dueState.stopped, id])] }),
    [dueState, writeDue],
  );

  const expand = useCallback(
    (lines: readonly string[]) => {
      push({ role: "assistant", text: lines.join("\n") });
    },
    [push],
  );

  const endConversation = useCallback(() => {
    setConversing(false);
    ear.current?.stop();
    ear.current = null;
    setListening(false);
    setHeard("");
  }, []);

  const endCall = useCallback(() => {
    stoodDown.current = true;
    endConversation();
    voice.stop();
    setSpeaking(false);
    setOnCall(false);
    setPresent(false);
    setOpen(false);
  }, [endConversation]);

  /*
   * The conversation loop.
   *
   * After she has finished answering — finished *speaking*, where there is a
   * voice, because otherwise the microphone opens onto her own words — the ear
   * goes back on. It stops the moment the operator signs off, closes her, or
   * goes quiet, so a live microphone is never left running by omission.
   */
  /*
   * Opening her opens the microphone.
   *
   * She was only ever listening after her name was called, so clicking her
   * picture gave a panel that heard nothing and a typing box — which is the
   * arrangement this was supposed to replace. Now: open, not quiet, hands-free
   * on, and a browser that can hear — she listens.
   */
  useEffect(() => {
    if (!open || !present || prefs.quiet || !prefs.voice.handsFree || micBlocked) return;
    if (!ears.isSupported() || conversing || listening || leaving || stoodDown.current) return;
    /* A call opens its own microphone, after the hello; opening one here as
       well cut the hello off and put two microphones on one voice. */
    if (onCall) return;
    const t = window.setTimeout(() => startListeningRef.current?.(), 350);
    return () => window.clearTimeout(t);
  }, [conversing, leaving, listening, micBlocked, onCall, open, prefs.quiet, prefs.voice.handsFree, present]);

  /*
   * Leaving.
   *
   * After "thanks" she says her goodbye, and only once that is out of the air
   * does the fade begin — a panel dissolving under its own voice reads as a
   * fault. The fade is the stylesheet's; when it is done she withdraws to the
   * corner tab, which is where her name brings her back from.
   */
  useEffect(() => {
    if (!fading) return;
    const t = window.setTimeout(() => {
      setLeaving(false);
      ear.current?.stop();
      ear.current = null;
      setListening(false);
      setHeard("");
      setOnCall(false);
      setPresent(false);
      setOpen(false);
    }, 1800);
    return () => window.clearTimeout(t);
  }, [fading]);

  /*
   * Interrupting her.
   *
   * Everywhere else in this module the microphone is closed while she talks,
   * which is what keeps her from hearing herself — and it also meant she could
   * not be stopped without reaching for the keyboard. For hands-free that is
   * the wrong way round: the moment worth interrupting is the middle of a long
   * answer, and that is exactly when the operator's hands are busy.
   *
   * So a second recogniser runs for the duration of her speech, listening for
   * a handful of stop words and nothing else. `isInterruption` discounts
   * anything she is saying herself, because the speaker and the microphone are
   * inches apart. Only while hands-free is on: an operator who has not asked
   * for an open microphone does not get one here either.
   */
  const barge = useRef<ears.WakeWatcher | null>(null);
  useEffect(() => {
    if (!speaking || !prefs.voice.handsFree || prefs.quiet || !ears.isSupported()) {
      barge.current?.stop();
      barge.current = null;
      return;
    }
    if (barge.current) return;
    barge.current = ears.listenForWake(
      interruptionWords,
      (heard) => {
        if (!isInterruption(heard, nowSpeaking.current)) return;
        barge.current?.stop();
        barge.current = null;
        voice.stop();
        nowSpeaking.current = "";
        ears.voiceLog(`sky: interrupted by "${heard}"`);
        stoodDown.current = true;
        setConversing(false);
        setSpeaking(false);
        setMark("idle");
      },
      /* A microphone that will not open here is not worth a sentence in the
         panel: the answer is still being read out, and Stop is on screen. */
      () => {
        barge.current = null;
      },
      voice.recognitionLang(prefs.voice.lang, prefs.voice.voiceURI),
    );
    return () => {
      barge.current?.stop();
      barge.current = null;
    };
  }, [prefs.quiet, prefs.voice.handsFree, prefs.voice.lang, prefs.voice.voiceURI, speaking]);

  useEffect(() => () => barge.current?.stop(), []);

  const lastSpoken = useRef<string | null>(null);
  useEffect(() => {
    if (!conversing || !open || prefs.quiet || listening || speaking || micBlocked || stoodDown.current) return;
    const last = turns[turns.length - 1];
    if (!last || last.role !== "assistant" || lastSpoken.current === last.id) return;
    /*
     * Half a second, and deliberately not driven by `mark`.
     *
     * The first version watched the mark as well, which flips from "speaking"
     * to "idle" 700 ms after every answer — so the effect re-ran, its cleanup
     * cancelled its own pending timer, and the ear never went back on. The turn
     * list and the two states that genuinely block a microphone are enough.
     */
    /*
     * Marked as answered only when the microphone actually opens. It used to
     * be marked first: when something else spoke inside the half second — a
     * screen's welcome line after she opened it — this timer was cancelled,
     * and when that speech ended the answer counted as handled, so the
     * microphone never reopened and only the stop-word listener heard the
     * next question.
     */
    const t = window.setTimeout(() => {
      lastSpoken.current = last.id;
      startListeningRef.current?.();
    }, 500);
    return () => window.clearTimeout(t);
  }, [conversing, listening, micBlocked, open, prefs.quiet, speaking, turns]);

  const clear = useCallback(() => setTurns([]), []);

  const api = useMemo<AssistantApi>(
    () => ({
      open,
      turns,
      mark,
      unread,
      config,
      setConfig,
      summon,
      dismiss,
      ask,
      noteAction,
      propose,
      expand,
      clear,
      who,
      prefs,
      setPrefs,
      level,
      screen,
      speaking,
      readAloud,
      stopSpeaking,
      present,
      show,
      withdraw,
      waking,
      conversing,
      endConversation,
      listening,
      heard,
      startListening: listenNow,
      onCall,
      endCall,
      stopListening,
      micBlocked,
      leaving: fading,
      due,
      ackDue,
      stopDue,
      reminders,
      addReminder,
      completeReminder,
      removeReminder,
    }),
    [
      addReminder,
      ask,
      clear,
      ackDue,
      completeReminder,
      config,
      due,
      expand,
      stopDue,
      dismiss,
      mark,
      noteAction,
      open,
      prefs,
      propose,
      readAloud,
      reminders,
      removeReminder,
      setConfig,
      conversing,
      endConversation,
      fading,
      heard,
      listening,
      micBlocked,
      present,
      setPrefs,
      show,
      waking,
      withdraw,
      speaking,
      listenNow,
      onCall,
      endCall,
      stopListening,
      stopSpeaking,
      summon,
      turns,
      unread,
      who,
    
      level,
      screen,],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAssistant(): AssistantApi {
  const a = useContext(Ctx);
  if (!a) throw new Error("useAssistant must be used inside AssistantProvider");
  return a;
}

/**
 * For screens that want to report an action without caring whether the dock is
 * mounted — returns a no-op outside the provider so a page can call it freely.
 */
export function useReportAction(): AssistantApi["noteAction"] {
  const a = useContext(Ctx);
  return a?.noteAction ?? (() => {});
}
